/**
 * Client-side spatial-compute foundation (M8). Pure, dependency-free geometry
 * over lng/lat coordinates — point-in-polygon, radius membership, geodesic
 * circles, centroid, convex hull, and a capacity-balanced partition — that all
 * five analysis tools draw from. Kept free of maplibre/turf/DOM so it is fully
 * unit-testable AND transferable to a Web Worker unchanged (see ./job.ts).
 *
 * The heavy predicate (point-in-polygon over 50k points) runs here on plain
 * arrays; ./job.ts wraps these in a typed request/response envelope so the same
 * code can move off the main thread when it would jank.
 */

import type { Bounds, Point } from "@/lib/map/pointset";
import { boundsOf, circleRing, haversineMeters, MILES_TO_METERS } from "@/lib/geo/geoMath";

/** A polygon outer ring: [lng, lat] vertices (open or closed). */
export type Ring = [number, number][];

// The great-circle + circle-ring math + the mile constant live ONCE in the geo
// foundation (`@/lib/geo/geoMath`; geo must not import analysis, so the shared
// code lives down there). Re-exported here so the analysis-layer call sites and
// the `@/lib/analysis` barrel keep resolving them from `./geometry` unchanged.
export { circleRing, haversineMeters, MILES_TO_METERS };

/**
 * Ray-casting point-in-ring test. `ring` is a list of [lng,lat] vertices; the
 * closing vertex is optional (the algorithm wraps the last→first edge itself).
 * Standard even-odd rule: robust for the simple, non-self-intersecting rings a
 * lasso / hull / admin boundary produces.
 */
export function pointInRing(lng: number, lat: number, ring: Ring): boolean {
  let inside = false;
  const n = ring.length;
  if (n < 3) return false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const vi = ring[i]!;
    const vj = ring[j]!;
    const xi = vi[0];
    const yi = vi[1];
    const xj = vj[0];
    const yj = vj[1];
    const intersects = yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

/** Row ids of every point enclosed by `ring`. */
export function pointsInRing(points: Point[], ring: Ring): string[] {
  const ids: string[] = [];
  for (const p of points) if (pointInRing(p.lng, p.lat, ring)) ids.push(p.id);
  return ids;
}

/** Row ids of every point within `radiusMeters` of `center` (great-circle). */
export function pointsInRadius(points: Point[], center: [number, number], radiusMeters: number): string[] {
  const ids: string[] = [];
  for (const p of points) if (haversineMeters(center, [p.lng, p.lat]) <= radiusMeters) ids.push(p.id);
  return ids;
}

/** Arithmetic centroid ([lng,lat]) of a set of points, or null when empty. */
export function centroid(points: Point[]): [number, number] | null {
  if (points.length === 0) return null;
  let sx = 0;
  let sy = 0;
  for (const p of points) {
    sx += p.lng;
    sy += p.lat;
  }
  return [sx / points.length, sy / points.length];
}

/** Centroid of a ring's vertices ([lng,lat]) — used for territory label placement. */
export function ringCentroid(ring: Ring): [number, number] | null {
  if (ring.length === 0) return null;
  let sx = 0;
  let sy = 0;
  for (const v of ring) {
    sx += v[0];
    sy += v[1];
  }
  return [sx / ring.length, sy / ring.length];
}

/** Bounding box of a set of points (same shape as PointSet bounds). */
export function bboxOf(points: Point[]): Bounds | null {
  return boundsOf(points);
}

/**
 * Convex hull ([lng,lat] ring, counter-clockwise, closed) of a set of points via
 * Andrew's monotone chain. Gives automated territories a fillable outline. With
 * fewer than 3 distinct points it returns whatever ring it can (0/1/2 vertices),
 * and the overlay simply skips a degenerate fill.
 */
export function convexHull(points: Point[]): Ring {
  const pts = points.map((p): [number, number] => [p.lng, p.lat]);
  const uniq = Array.from(new Map(pts.map((p) => [`${p[0]},${p[1]}`, p])).values());
  if (uniq.length < 3) return uniq;
  uniq.sort((a, b) => (a[0] === b[0] ? a[1] - b[1] : a[0] - b[0]));
  const cross = (o: [number, number], a: [number, number], b: [number, number]): number =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: Ring = [];
  for (const p of uniq) {
    while (lower.length >= 2 && cross(lower[lower.length - 2]!, lower[lower.length - 1]!, p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper: Ring = [];
  for (let i = uniq.length - 1; i >= 0; i--) {
    const p = uniq[i]!;
    while (upper.length >= 2 && cross(upper[upper.length - 2]!, upper[upper.length - 1]!, p) <= 0) upper.pop();
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  const hull = lower.concat(upper);
  if (hull.length > 0) hull.push(hull[0]!); // close the ring
  return hull;
}
